import { createSlice } from "@reduxjs/toolkit";

/* -------------------------------------------------------------------------- */
/* Address Factory                                                            */
/* -------------------------------------------------------------------------- */

const createAddress = () => ({
  buildingNumber: null,
  street: "",
  town: "",
  county: null,
  postcode: "",
  country: null,
  purchaseStatus: "",
  houseSize: "",
  distance: 0,
  volume: 0,
  volumeUnit: "m3",
});

/* -------------------------------------------------------------------------- */
/* Storage Local Form                                                         */
/* -------------------------------------------------------------------------- */

export const initialStorageLocalState = {
  referrerId: "",
  clientReferrerId: "",
  customer: {
    name: "",
    phone: "",
    prefix: "",
    accountName: "",
    email: "",
  },

  // Multiple collection addresses
  collectionAddress: [
    {
      buildingNumber: null,
      street: "",
      town: "",
      county: null,
      postcode: "",
      country: null,
      purchaseStatus: "",
      houseSize: "",
      distance: 0,
      volume: 0,
      volumeUnit: "m3",
    },
  ],

  // Multiple delivery addresses
  deliveryAddress: [createAddress()],
  followUpDate: "",
  movingOnDate: "",
  movingOnEndDate: "",
  packingOnDate: "",
  returningDistance: 0,
  customerNotes: "",
  advertId: "",
  promoCode: "",
  promoSource: "",
  salesPersonId: "",
  salesPersonName: "",
  leadTypeId: "",
};

/* -------------------------------------------------------------------------- */
/* Initial State                                                              */
/* -------------------------------------------------------------------------- */

const initialState = {
  /* ------------------------------------------------------------------------ */
  /* Storage List                                                             */
  /* ------------------------------------------------------------------------ */

  storageList: [],

  storagePagination: {},

   /* ------------------------------------------------------------------------ */
  /* Storage Log List                                                          */
  /* ------------------------------------------------------------------------ */
  storageLogLists:[],
  storageLogListPagination:{},
  storageLogRefreshVersion: 0,

  /* ------------------------------------------------------------------------ */
  /* Storage Details                                                          */
  /* ------------------------------------------------------------------------ */

  storageDetails: null,

  /* ------------------------------------------------------------------------ */
  /* Add / Update Storage Form                                                */
  /* ------------------------------------------------------------------------ */

  newStorageLocal: initialStorageLocalState,

  /* ------------------------------------------------------------------------ */
  /* API Responses                                                            */
  /* ------------------------------------------------------------------------ */

  response: {
    addStorage: false,
    deleteStorage: false,
    updateStorage: false,
  },
  createdStorageResponse: null,
};

const saveLeadStorageDetailsToSession = (storageDetails: any) => {
  sessionStorage.setItem("storageDetails", JSON.stringify(storageDetails));
};
/* -------------------------------------------------------------------------- */
/* Storage Slice                                                              */
/* -------------------------------------------------------------------------- */

export const storageSlice = createSlice({
  name: "storage",

  initialState,

  reducers: {
    /* ====================================================================== */
    /* Get All Storage                                                        */
    /* ====================================================================== */

    getAllStorageAction: (state, { payload }) => {},

    setAllStorageAction: (state, { payload }) => {
      const { list = [], pagination = {} } = payload || {};

      state.storageList = list;
      state.storagePagination = pagination;
    },

    /* ====================================================================== */
    /* Get Single Storage                                                     */
    /* ====================================================================== */

    getSingleStorageAction: (state, { payload }) => {},

    setSingleStorageAction: (state, { payload }) => {
      state.storageDetails = payload;
      saveLeadStorageDetailsToSession(payload);
    },

    /* ====================================================================== */
    /* Get Storage Logs                                                      */
    /* ====================================================================== */
    getStorageLogList:(state, {payload})=>{},
    setStorageLogResponse: (state,{payload})=>{
      const { list = [], pagination = {} } = payload || {};
      state.storageLogLists = list;
      state.storageLogListPagination = pagination;
    },
    refreshStorageLogList: (state) => {
      state.storageLogRefreshVersion += 1;
    },
    /* ====================================================================== */
    /* Add Storage                                                            */
    /* ====================================================================== */

    addStorageAction: (state, { payload }) => {},

    setStorageResponse: (state, { payload }) => {
      state.response.addStorage = payload;
    },

    setCreatedStorageResponse: (state, { payload }) => {
      state.createdStorageResponse = payload;
    },

    /* ====================================================================== */
    /* Update Storage                                                         */
    /* ====================================================================== */

    updateStorageAction: (state, { payload }) => {},

    setUpdateStorageResponse: (state, { payload }) => {
      state.response.updateStorage = payload;
    },

    /* ====================================================================== */
    /* Delete Storage                                                         */
    /* ====================================================================== */

    deleteStorageAction: () => {},

    setDeleteStorageResponse: (state, { payload }) => {
      state.response.deleteStorage = payload;
    },

    /* ====================================================================== */
    /* Storage Local Form                                                     */
    /* ====================================================================== */

    setNewStorageLocal: (state, { payload }) => {
      if (payload === false || payload === null || payload === undefined) {
        state.newStorageLocal = structuredClone(initialStorageLocalState);
        return;
      }

      state.newStorageLocal = {
        ...structuredClone(initialStorageLocalState),
        ...payload,
        customer: {
          ...structuredClone(initialStorageLocalState.customer),
          ...(payload?.customer || {}),
        },
        collectionAddress: Array.isArray(payload?.collectionAddress)
          ? payload.collectionAddress
          : structuredClone(initialStorageLocalState.collectionAddress),
        deliveryAddress: Array.isArray(payload?.deliveryAddress)
          ? payload.deliveryAddress
          : structuredClone(initialStorageLocalState.deliveryAddress),
      };
    },

    resetNewStorageLocal: (state) => {
      state.newStorageLocal = structuredClone(initialStorageLocalState);
    },

    /* ====================================================================== */
    /* Clear Storage Details                                                  */
    /* ====================================================================== */

    clearStorageDetails: (state) => {
      state.storageDetails = null;
    },

    /* ====================================================================== */
    /* Reset API Responses                                                    */
    /* ====================================================================== */

    resetStorageResponses: (state) => {
      state.response = {
        addStorage: false,
        deleteStorage: false,
        updateStorage: false,
      };
    },
  },
});

/* -------------------------------------------------------------------------- */
/* Actions                                                                    */
/* -------------------------------------------------------------------------- */

export const {
  // Get All
  getAllStorageAction,
  setAllStorageAction,

  // Get Single
  getSingleStorageAction,
  setSingleStorageAction,

  // logs
  getStorageLogList,
  setStorageLogResponse,
  refreshStorageLogList,

  // Add
  addStorageAction,
  setStorageResponse,
  setCreatedStorageResponse,

  // Update
  updateStorageAction,
  setUpdateStorageResponse,

  // Delete
  deleteStorageAction,
  setDeleteStorageResponse,

  // Local Form
  setNewStorageLocal,
  resetNewStorageLocal,

  // Clear
  clearStorageDetails,

  // Reset
  resetStorageResponses,
} = storageSlice.actions;

/* -------------------------------------------------------------------------- */
/* Reducer                                                                    */
/* -------------------------------------------------------------------------- */

export default storageSlice.reducer;
